import React from "react";
import { useTranslation } from "react-i18next";
import { Activity, TrendingUp, Zap, AlertOctagon, ArrowUpRight } from "lucide-react";
import { fmtMoney } from "../../lib/money";

/**
 * QENVA · Business KPI Grid (4 cards per Figma).
 *
 *  EXPOSITION FINANCIÈRE | VALEUR PRÉSERVÉE™ | OPPORTUNITÉS | RISQUES CRITIQUES
 *  248 400 $              38 200 $              6 ident.        2 actifs
 *  24 docs                Ce mois               127 400 $       120 000 $ exposés
 *                                                               (rail rouge gauche + badge "À TRAITER")
 */
function buildCardDefs(t) {
  return [
    {
      key: "exposure_financial",
      icon: Activity,
      iconClass: "bg-zinc-100 text-zinc-600",
      label: t("business_kpis.exposure"),
      formatValue: (kpi) => fmtMoney(kpi.amount, kpi.currency, { compact: false }),
      formatSub: (kpi) => t("business_kpis.doc_count", { count: kpi.docs_count ?? 0 }),
      subClass: "text-zinc-500",
    },
    {
      key: "preserved_value",
      icon: TrendingUp,
      iconClass: "bg-emerald-50 text-emerald-600",
      label: t("business_kpis.preserved_value"),
      formatValue: (kpi) => fmtMoney(kpi.amount, kpi.currency, { compact: false }),
      formatSub: () => t("business_kpis.this_month"),
      subClass: "text-emerald-600",
      accentTrend: true,
    },
    {
      key: "opportunities",
      icon: Zap,
      iconClass: "bg-amber-50 text-amber-600",
      label: t("business_kpis.opportunities"),
      formatValue: (kpi) => t("business_kpis.identified_count", { count: kpi.count ?? 0 }),
      formatSub: (kpi) => fmtMoney(kpi.potential_amount, kpi.currency, { compact: false }),
      subClass: "text-emerald-600 font-bold tabular-nums",
      accentTrend: true,
    },
    {
      key: "critical_risks",
      icon: AlertOctagon,
      iconClass: "bg-rose-50 text-rose-600",
      label: t("business_kpis.critical_risks"),
      formatValue: (kpi) => t("business_kpis.active_count", { count: kpi.count ?? 0 }),
      formatSub: (kpi) => `${fmtMoney(kpi.exposed_amount, kpi.currency, { compact: false })} ${t("business_kpis.exposed_suffix")}`,
      subClass: "text-rose-600",
      badge: t("business_kpis.to_process_badge"),
      badgeClass: "bg-rose-50 text-rose-600 ring-1 ring-rose-200",
      rail: "bg-rose-500",
      railWhenZero: true,
    },
  ];
}

export default function BusinessKPIGrid({ businessKpis, testId = "business-kpi-grid" }) {
  const { t } = useTranslation("dashboard");
  const k = businessKpis || {};
  const CARD_DEFS = buildCardDefs(t);
  return (
    <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={testId}>
      {CARD_DEFS.map((def) => {
        const kpi = k[def.key] || {};
        const showRail = def.rail && (def.railWhenZero || (kpi.count ?? 0) > 0 || (kpi.amount ?? 0) > 0);
        const Icon = def.icon;
        const showBadge = def.badge && ((kpi.count ?? 0) > 0 || (kpi.amount ?? 0) > 0);
        return (
          <article
            key={def.key}
            className="relative rounded-2xl border border-zinc-200 bg-white overflow-hidden flex"
            data-testid={`${testId}-${def.key.replace(/_/g, "-")}`}
          >
            {showRail && <div className={`w-1 shrink-0 ${def.rail}`} />}
            <div className="flex-1 p-5">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl grid place-items-center ${def.iconClass}`}>
                  <Icon size={17} strokeWidth={1.75} />
                </div>
                {showBadge ? (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${def.badgeClass}`}>
                    {def.badge}
                  </span>
                ) : def.accentTrend && (
                  <ArrowUpRight size={14} className="text-emerald-500" />
                )}
              </div>
              <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-2">
                {def.label}
              </p>
              <p
                className="font-display font-bold text-qenva-navy text-[26px] tabular-nums leading-none"
                data-testid={`${testId}-${def.key.replace(/_/g, "-")}-value`}
              >
                {def.formatValue(kpi)}
              </p>
              <p className={`text-[12px] mt-3 ${def.subClass}`} data-testid={`${testId}-${def.key.replace(/_/g, "-")}-sub`}>
                {def.formatSub(kpi)}
              </p>
            </div>
          </article>
        );
      })}
    </section>
  );
}
